import { motion } from 'framer-motion';
import { useRef, useCallback } from 'react';
import { X, Share2, Download, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { playClick, playComplete } from '../utils/sounds';

export interface ShareData {
  type: 'achievement' | 'report' | 'daily';
  title: string;
  playerName: string;
  stats: { label: string; value: string; color?: string }[];
  achievement?: {
    icon: string;
    name: string;
    description: string;
    tier: 'bronze' | 'silver' | 'gold';
  };
  summary?: string;
}

const TIER_GRADIENTS = {
  bronze: 'from-amber-400 to-orange-500',
  silver: 'from-gray-300 to-slate-400',
  gold: 'from-yellow-300 to-amber-500',
};

export default function ShareModal({ data, onClose }: { data: ShareData; onClose: () => void }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const getShareText = useCallback(() => {
    let text = `🎮 视界探险 - ${data.title}\n`;
    text += `👤 ${data.playerName || '小探险家'}\n`;
    if (data.achievement) {
      text += `🏆 ${data.achievement.icon} ${data.achievement.name}\n`;
      text += `📝 ${data.achievement.description}\n`;
    }
    text += `\n📊 数据:\n`;
    data.stats.forEach(s => {
      text += `  ${s.label}: ${s.value}\n`;
    });
    if (data.summary) {
      text += `\n💬 ${data.summary}\n`;
    }
    text += `\n🔗 一起来训练视力吧！`;
    return text;
  }, [data]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getShareText());
      setCopied(true);
      playComplete();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = getShareText();
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      playComplete();
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    playClick();
    if (navigator.share) {
      try {
        await navigator.share({
          title: `视界探险 - ${data.title}`,
          text: getShareText(),
        });
      } catch {
        // User cancelled or not supported
      }
    } else {
      handleCopy();
    }
  };

  const handleSaveImage = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    playClick();

    try {
      // Use canvas to render the card
      const card = cardRef.current;
      const canvas = document.createElement('canvas');
      const scale = 2;
      canvas.width = card.offsetWidth * scale;
      canvas.height = card.offsetHeight * scale;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(scale, scale);

      // Background
      const gradient = ctx.createLinearGradient(0, 0, card.offsetWidth, card.offsetHeight);
      if (data.type === 'achievement') {
        gradient.addColorStop(0, '#6366f1');
        gradient.addColorStop(1, '#8b5cf6');
      } else if (data.type === 'daily') {
        gradient.addColorStop(0, '#f59e0b');
        gradient.addColorStop(1, '#ea580c');
      } else {
        gradient.addColorStop(0, '#059669');
        gradient.addColorStop(1, '#0d9488');
      }
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.roundRect(0, 0, card.offsetWidth, card.offsetHeight, 24);
      ctx.fill();

      // Decorative circles
      ctx.globalAlpha = 0.1;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(card.offsetWidth - 40, 40, 60, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(30, card.offsetHeight - 30, 40, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      // Title
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.font = '12px Inter, system-ui, sans-serif';
      ctx.fillText('视界探险', 24, 36);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 20px Inter, system-ui, sans-serif';
      ctx.fillText(data.title, 24, 64);

      // Player name
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.font = '11px Inter, system-ui, sans-serif';
      ctx.fillText(`👤 ${data.playerName || '小探险家'}`, 24, 88);

      // Achievement icon
      if (data.achievement) {
        ctx.font = '40px serif';
        ctx.fillText(data.achievement.icon, card.offsetWidth / 2 - 20, 140);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 16px Inter, system-ui, sans-serif';
        const nameWidth = ctx.measureText(data.achievement.name).width;
        ctx.fillText(data.achievement.name, (card.offsetWidth - nameWidth) / 2, 170);
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.font = '11px Inter, system-ui, sans-serif';
        const descWidth = ctx.measureText(data.achievement.description).width;
        ctx.fillText(data.achievement.description, (card.offsetWidth - descWidth) / 2, 190);
      }

      // Stats
      const statsY = data.achievement ? 220 : 120;
      const statWidth = (card.offsetWidth - 48) / Math.min(data.stats.length, 3);
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.beginPath();
      ctx.roundRect(16, statsY - 10, card.offsetWidth - 32, 70, 12);
      ctx.fill();

      data.stats.slice(0, 3).forEach((stat, i) => {
        const x = 24 + i * statWidth;
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 18px Inter, system-ui, sans-serif';
        ctx.fillText(stat.value, x, statsY + 24);
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.font = '10px Inter, system-ui, sans-serif';
        ctx.fillText(stat.label, x, statsY + 44);
      });

      // Summary
      if (data.summary) {
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.font = '11px Inter, system-ui, sans-serif';
        ctx.fillText(data.summary, 24, statsY + 90);
      }

      // Footer
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '9px Inter, system-ui, sans-serif';
      const footerText = '一起来训练视力吧！ · 视界探险 🎮';
      ctx.fillText(footerText, 24, card.offsetHeight - 16);

      // Date
      const dateText = new Date().toLocaleDateString('zh-CN');
      const dateWidth = ctx.measureText(dateText).width;
      ctx.fillText(dateText, card.offsetWidth - dateWidth - 24, card.offsetHeight - 16);

      // Download
      const link = document.createElement('a');
      const typeName = data.type === 'achievement' ? '成就' : data.type === 'daily' ? '每日挑战' : '战绩';
      link.download = `视界探险-${typeName}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      playComplete();
    } catch (err) {
      console.error('Save image failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 20 }}
        className="w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <div className="flex justify-end mb-2">
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Share Card */}
        <div
          ref={cardRef}
          className={`rounded-3xl p-6 shadow-2xl relative overflow-hidden ${
            data.type === 'achievement'
              ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
              : data.type === 'daily'
              ? 'bg-gradient-to-br from-amber-500 to-orange-600'
              : 'bg-gradient-to-br from-emerald-500 to-teal-600'
          }`}
          style={{ minHeight: data.achievement ? '320px' : '260px' }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-x-4 -translate-y-12" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-x-2 translate-y-8" />
          <div className="absolute top-1/2 right-4 w-16 h-16 bg-white/5 rounded-full" />

          {/* Header */}
          <div className="relative z-10">
            <p className="text-white/70 text-[10px] font-medium tracking-wide">视界探险</p>
            <h2 className="text-white text-lg font-bold mt-0.5">{data.title}</h2>
            <p className="text-white/60 text-[11px] mt-0.5">👤 {data.playerName || '小探险家'}</p>
          </div>

          {/* Achievement display */}
          {data.achievement && (
            <div className="relative z-10 text-center mt-5 mb-4">
              <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${TIER_GRADIENTS[data.achievement.tier]} flex items-center justify-center text-3xl shadow-lg`}>
                {data.achievement.icon}
              </div>
              <p className="text-white font-bold text-base mt-2">{data.achievement.name}</p>
              <p className="text-white/60 text-[11px] mt-0.5">{data.achievement.description}</p>
            </div>
          )}

          {/* Stats */}
          <div className={`relative z-10 ${data.achievement ? 'mt-3' : 'mt-6'}`}>
            <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4">
              <div className={`grid grid-cols-${Math.min(data.stats.length, 3)} gap-3`}>
                {data.stats.slice(0, 3).map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="text-white text-lg font-bold">{stat.value}</p>
                    <p className="text-white/60 text-[9px] mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          {data.summary && (
            <p className="relative z-10 text-white/70 text-[11px] mt-3 text-center italic">
              "{data.summary}"
            </p>
          )}

          {/* Footer */}
          <div className="relative z-10 flex items-center justify-between mt-4 pt-3 border-t border-white/10">
            <span className="text-white/40 text-[9px]">一起来训练视力吧！ 🎮</span>
            <span className="text-white/40 text-[9px]">{new Date().toLocaleDateString('zh-CN')}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button
            onClick={handleSaveImage}
            disabled={saving}
            className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 transition-colors text-white active:scale-95"
          >
            <Download className="w-5 h-5" />
            <span className="text-[10px]">{saving ? '保存中...' : '保存图片'}</span>
          </button>
          <button
            onClick={handleCopy}
            className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 transition-colors text-white active:scale-95"
          >
            {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
            <span className="text-[10px]">{copied ? '已复制' : '复制文字'}</span>
          </button>
          <button
            onClick={handleShare}
            className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 transition-colors text-white active:scale-95"
          >
            <Share2 className="w-5 h-5" />
            <span className="text-[10px]">分享</span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
